import FormFooter from "components/Footers/FormFooter";
import FormField from "components/Forms/FormField";
import LoadingOverlay from "components/Loaders/LoadingOverlay";
import LicenceOptionMapper from "components/Mappers/LicenceOptionMapper";
import OrganisationOptionMapper from "components/Mappers/OrganisationOptionMapper";
import AlertMessage from "components/Message/AlertMessage";
import ModalComponent from "components/Modal/ModalComponent";
import { fetchHandleCreateAccount } from "helpers/Account/AccountHelpers";
import {
  defaultAccountErrorState,
  defaultCreateAccountRequest,
} from "helpers/constants/AccountConstants";
import { TokenContext } from "helpers/constants/Contexts";
import { findOrganisation } from "helpers/Organisations/OrganisationHelpers";
import { IAccount } from "interfaces/Account/IAccount";
import { ICreateAccountRequest } from "interfaces/Account/ICreateAccountRequest";
import { IButtonProps } from "interfaces/General/IButtonProps";
import { ILicenceState } from "interfaces/Licence/ILicenseState";
import { IOrganisationsState } from "interfaces/Organisation/IOrganisationsState";
import moment from "moment";
import {
  ChangeEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Form, Stack } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  licensesStateSelector,
  organisationsStateSelector,
} from "redux/states/stateSelectors";

import styles from "./CreateAccount.module.scss";

interface FormError {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  licenceType: boolean;
  licenseExpiry: boolean;
}

const CreateAccountComponent = () => {
  //#region selectors and hooks
  const licenceTypesState: ILicenceState = useSelector(licensesStateSelector);
  const organisationsState: IOrganisationsState = useSelector(
    organisationsStateSelector
  );
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const accessToken = useContext(TokenContext);
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const { handleSubmit } = useForm();
  const navigate = useNavigate();
  //#endregion

  //#region memoized values
  const successMessageEmail = useMemo(
    () => (searchParams.has("email") ? searchParams.get("email") : undefined),
    [searchParams]
  );

  const cancelUrl = useMemo(
    () => (orgId ? `/organisations/${orgId}` : "/organisations"),
    [orgId]
  );
  //#endregion

  //#region state
  const [disableOrganisationCombobox, setDisableOrganisationCombobox] =
    useState(false);
  const [account, setAccount] = useState<ICreateAccountRequest>(
    defaultCreateAccountRequest
  );
  const [validated, setValidated] = useState(false);
  const [errorState, setErrorState] = useState<FormError>(
    defaultAccountErrorState
  );
  const [disabled, setDisabled] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [failure, setFailure] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState(
    "Creating account..."
  );
  //#endregion

  //#region validation and effects
  // Validate form fields whenever account changes
  const displayMessage = useCallback((message: string, success: boolean) => {
    setMessage(message);
    setFailure(!success);
    setShowMessage(true);
  }, []);

  const validate = useCallback(() => {
    setErrorState({
      firstName: account.firstName.trim().length === 0,
      lastName: account.lastName.trim().length === 0,
      email: !/\S+@\S+\.\S+/.test(account.email),
      licenceType: account.licenceTypeId.trim().length === 0,
      licenseExpiry:
        account.licenceExpiry.trim().length === 0 ||
        moment.utc(account.licenceExpiry).isBefore(moment.utc()),
    });
  }, [account]);

  // Run validation when account changes
  useEffect(() => {
    validate();
  }, [account, validate]);

  // Set organization if orgId is provided in URL
  useEffect(() => {
    if (orgId) {
      const org = findOrganisation(orgId, organisationsState.organisations);
      if (org) {
        setAccount((prev) => ({
          ...prev,
          organisationId: org.organisationId,
        }));
        setDisableOrganisationCombobox(true);
      }
    }
  }, [orgId, organisationsState.organisations]);

  // Display validation messages
  useEffect(() => {
    if (validated) {
      displayMessage("Please fill out the required fields.", false);
      window.scrollTo(0, 0);
    }
  }, [displayMessage, validated]);

  // Display success message
  useEffect(() => {
    if (successMessageEmail) {
      displayMessage(
        `Successfully Created Account for ${successMessageEmail}`,
        true
      );
    }
  }, [displayMessage, successMessageEmail]);
  //#endregion

  //#region event handlers
  const handleChange: ChangeEventHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      setAccount((prev) => ({ ...prev, [name]: value }));
    },
    [setAccount]
  );

  const onCreateAccountSuccess = useCallback(
    (account: IAccount) => {
      setIsSubmitting(false);
      navigate(
        `${location.pathname.replace(
          "/success",
          ""
        )}/success?email=${encodeURIComponent(account.email)}`
      );
    },
    [navigate, location.pathname]
  );

  const onCreateAccountError = useCallback(
    (message: string) => {
      setIsSubmitting(false);
      setDisabled(false);
      displayMessage(`Could not create account for ${account.email}`, false);
      window.scrollTo(0, 0);
    },
    [account.email, displayMessage]
  );

  const sendCreateAccountRequest = useCallback(
    async (account: ICreateAccountRequest) => {
      setDisabled(true);
      setIsSubmitting(true);
      setSubmissionMessage("Creating account...");
      dispatch(
        fetchHandleCreateAccount(
          accessToken,
          account,
          onCreateAccountSuccess,
          onCreateAccountError
        )
      );
    },
    [accessToken, dispatch, onCreateAccountSuccess, onCreateAccountError]
  );

  const onCancel = useCallback(() => {
    if (
      JSON.stringify(account) !== JSON.stringify(defaultCreateAccountRequest)
    ) {
      setShowAlert(true);
    } else {
      navigate(cancelUrl);
    }
  }, [account, navigate, cancelUrl]);

  const handleClose = useCallback(() => {
    setShowAlert(false);
  }, []);

  const onSubmit = useCallback(() => {
    window.scrollTo(0, 0);

    if (
      !errorState.firstName &&
      !errorState.lastName &&
      !errorState.email &&
      !errorState.licenceType &&
      !errorState.licenseExpiry
    ) {
      sendCreateAccountRequest(account);
    } else {
      setValidated(true);
    }
  }, [account, errorState, sendCreateAccountRequest]);
  //#endregion

  //#region button props
  const primaryBtn: IButtonProps = useMemo(
    () => ({
      text: "Submit",
      type: "submit",
      onClick: onSubmit,
      disabled: disabled,
    }),
    [disabled, onSubmit]
  );

  const secondaryBtn: IButtonProps = useMemo(
    () => ({
      text: "Cancel",
      onClick: () => onCancel(),
      disabled: disabled,
    }),
    [disabled, onCancel]
  );
  //#endregion

  return (
    <>
      <div className="form-page">
        <div className="vstack gap-4 col-md-5 mx-auto">
          <h3 className="text-heading-1">Account Details</h3>
          {showMessage && (
            <AlertMessage
              success={!failure}
              title={failure ? "Error" : "Success"}
              message={message}
            />
          )}
          <Form
            noValidate={true}
            onSubmit={handleSubmit(onSubmit)}
            className={styles.form}
          >
            <Stack direction="vertical" gap={4}>
              <FormField
                label="First Name"
                helpText="Enter the first name for this account"
                name="firstName"
                placeholder="John"
                value={account.firstName}
                error={errorState.firstName}
                disabled={disabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              />

              <FormField
                label="Last Name"
                helpText="Enter the Last name for this account"
                name="lastName"
                placeholder="Smith"
                value={account.lastName}
                error={errorState.lastName}
                disabled={disabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              />

              <FormField
                label="Email"
                helpText="Enter the email address for this account"
                name="email"
                type="email"
                placeholder="john.smith@email.com"
                value={account.email}
                error={errorState.email}
                disabled={disabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              />

              <FormField
                label="Licence Type"
                helpText="Select the licecne Type for this account"
                name="licenceTypeId"
                error={errorState.licenceType}
                disabled={disabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              >
                <Form.Select
                  size="lg"
                  name="licenceTypeId"
                  value={account.licenceTypeId}
                  onChange={handleChange}
                  disabled={disabled}
                  isInvalid={errorState.licenceType && validated}
                  isValid={!errorState.licenceType && validated}
                >
                  <LicenceOptionMapper
                    licenceTypes={licenceTypesState.licences}
                  />
                </Form.Select>
              </FormField>

              <FormField
                label="Organisation"
                helpText="Select the organisation for this account"
                name="organisationId"
                disabled={disabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
              >
                <Form.Select
                  size="lg"
                  name="organisationId"
                  value={account.organisationId}
                  onChange={handleChange}
                  disabled={disabled || disableOrganisationCombobox}
                  isValid={validated}
                >
                  <OrganisationOptionMapper
                    organisations={organisationsState.organisations}
                  />
                </Form.Select>
              </FormField>

              <FormField
                label="Licence Expiry Date"
                helpText="Enter the date you want this account to be available until"
                name="licenceExpiry"
                type="date"
                value={account.licenceExpiry}
                error={errorState.licenseExpiry}
                disabled={disabled}
                validated={validated}
                onChange={handleChange}
                className={styles.formGroup}
                minDate={moment().format("YYYY-MM-DD")}
              />
            </Stack>
          </Form>
        </div>
      </div>
      <FormFooter primaryBtn={primaryBtn} secondaryBtn={secondaryBtn} />
      <ModalComponent
        actionHandler={() => navigate(cancelUrl)}
        handleClose={handleClose}
        displayModal={showAlert}
        action={"Yes"}
        modalTitle={"Cancel Account Changes"}
        modalBody={
          "Your unsaved changes will be lost are you sure you want to cancel?"
        }
      />
      <LoadingOverlay isLoading={isSubmitting} message={submissionMessage} />
    </>
  );
};

export default CreateAccountComponent;
